import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  Button,
  Paper,
  CircularProgress,
  Container,
  Divider,
  Grid,
  TextField,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  IconButton,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import SaveIcon from "@mui/icons-material/Save";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import DeleteIcon from "@mui/icons-material/Delete";
import AssignmentService from "../services/AssignmentService";
import type { Assignment } from "../services/AssignmentService";
import { useSnackbar } from "notistack";
import ScatterPlotIcon from "@mui/icons-material/ScatterPlot";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import BuildIcon from "@mui/icons-material/Build";
import { Forklift } from "lucide-react";

const Debriefing = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Options valides pour le niveau de carburant
  const fuelLevelOptions = ["1/4", "1/3", "1/2", "Plein"];

  // États pour les données de fin d'affectation
  const [endData, setEndData] = useState({
    endDate: new Date().toISOString().split("T")[0],
    endTime: new Date().toTimeString().split(" ")[0].substring(0, 5),
    endMileage: 0,
    endFuelLevel: "Plein", // Valeur par défaut valide
    comments: "",
  });

  // États pour le retour d'équipement
  const [equipmentReturn, setEquipmentReturn] = useState({
    returned: true,
    condition: "bon_etat" as "bon_etat" | "endommagé" | "non_rendu",
    comment: "",
    photos: [] as File[],
    photoPreviews: [] as string[],
  });

  // Nettoyer les URLs d'objets lors du démontage
  useEffect(() => {
    return () => {
      equipmentReturn.photoPreviews.forEach((preview) => {
        URL.revokeObjectURL(preview);
      });
    };
  }, []);

  // Charger les détails de l'affectation
  useEffect(() => {
    const fetchAssignment = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await AssignmentService.getById(id);
        console.log("[Debriefing] Affectation récupérée:", data);

        // Vérifier si l'affectation est dans la bonne étape
        if (data.currentStep !== "debriefing") {
          console.log(
            "[Debriefing] L'étape actuelle ne correspond pas à celle attendue:",
            data.currentStep
          );
          setAssignment(data);
          setError(
            `L'étape actuelle (${data.currentStep}) ne correspond pas au débriefing`
          );
        } else {
          // L'étape est correcte, initialiser normalement
          setAssignment(data);
          // Pré-remplir le kilométrage final avec une valeur supérieure au kilométrage initial
          setEndData((prev) => ({
            ...prev,
            endMileage: (data.startMileage || 0) + 1,
          }));
        }
      } catch (err) {
        console.error(
          "[Debriefing] Erreur lors du chargement de l'affectation:",
          err
        );
        setError("Impossible de charger les détails de l'affectation");
        enqueueSnackbar("Erreur lors du chargement de l'affectation", {
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAssignment();
  }, [id, enqueueSnackbar]);

  // Gérer les changements dans les champs de formulaire
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setEndData((prev) => ({
      ...prev,
      [name]: name === "endMileage" ? Number(value) : value,
    }));
  };

  // Gérer les changements dans le menu déroulant
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setEndData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Valider le formulaire
  const isFormValid = () => {
    // Le kilométrage final doit être supérieur ou égal au kilométrage initial
    if (assignment && endData.endMileage < (assignment.startMileage || 0)) {
      return false;
    }
    return true;
  };

  // Finaliser l'affectation
  const handleFinalize = async () => {
    if (!id || !assignment) return;
    if (!isFormValid()) {
      enqueueSnackbar(
        "Le kilométrage final doit être supérieur au kilométrage initial",
        {
          variant: "warning",
        }
      );
      return;
    }

    try {
      setSubmitting(true);
      console.log("[Debriefing] Finalisation de l'affectation");

      // Préparer les données de fin avec le retour d'équipement si applicable
      const finalEndData: any = { ...endData };
      
      if (assignment.equipment?.taken) {
        // Préparer FormData si des photos sont présentes
        if (equipmentReturn.photos.length > 0) {
          const formData = new FormData();
          formData.append("endDate", finalEndData.endDate);
          formData.append("endTime", finalEndData.endTime);
          formData.append("endMileage", String(finalEndData.endMileage));
          formData.append("endFuelLevel", finalEndData.endFuelLevel);
          formData.append("comments", finalEndData.comments || "");
          
          // Ajouter les données d'équipement
          formData.append("equipment[returned]", String(equipmentReturn.returned && equipmentReturn.condition !== "non_rendu"));
          formData.append("equipment[returnCondition]", equipmentReturn.condition);
          formData.append("equipment[returnComment]", equipmentReturn.comment || "");
          
          // Ajouter les photos
          equipmentReturn.photos.forEach((photo) => {
            formData.append("equipment[photos]", photo);
          });

          // Envoyer avec FormData
          const token = localStorage.getItem("token");
          const response = await fetch(`${import.meta.env.VITE_API_URL || "/api"}/assignments/${id}/end`, {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || "Erreur lors de la finalisation");
          }
        } else {
          finalEndData.equipment = {
            ...assignment.equipment,
            returned: equipmentReturn.returned && equipmentReturn.condition !== "non_rendu",
            returnCondition: equipmentReturn.condition,
            returnComment: equipmentReturn.comment,
          };
          await AssignmentService.endAssignment(id, finalEndData);
        }
      } else {
        await AssignmentService.endAssignment(id, finalEndData);
      }

      enqueueSnackbar("Affectation finalisée avec succès", {
        variant: "success",
      });

      // Rediriger vers la liste des affectations
      navigate("/assignments");
    } catch (err) {
      console.error(
        "[Debriefing] Erreur lors de la finalisation de l'affectation:",
        err
      );
      enqueueSnackbar("Erreur lors de la finalisation de l'affectation", {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Gérer le retour à l'étape précédente
  const handlePreviousStep = async () => {
    if (!id) return;

    try {
      setSubmitting(true);
      console.log(
        "[Debriefing] Début du processus de retour à l'étape précédente"
      );

      // Récupérer d'abord l'état actuel pour s'assurer que nous avons les dernières données
      console.log("[Debriefing] Récupération de l'état actuel");
      const currentAssignment = await AssignmentService.getById(id);
      console.log(
        "[Debriefing] État actuel avant previousStep:",
        currentAssignment
      );

      // Passer à l'étape précédente
      console.log("[Debriefing] Appel de previousStep");
      const updatedAssignment = await AssignmentService.previousStep(id);
      console.log("[Debriefing] État après previousStep:", updatedAssignment);

      // Mettre à jour l'affectation locale
      setAssignment(updatedAssignment);

      // Redirection vers la page précédente (signatures de retour)
      console.log("[Debriefing] Redirection vers signatures (retour)");
      navigate(`/signatures/${id}?return=true`);
    } catch (err) {
      console.error(
        "[Debriefing] Erreur lors du retour à l'étape précédente:",
        err
      );
      enqueueSnackbar("Erreur lors du retour à l'étape précédente", {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Afficher si un élément est conforme ou non
  const renderComplianceStatus = (isCompliant: boolean, label: string) => (
    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
      {isCompliant ? (
        <CheckCircleIcon color="success" sx={{ mr: 1 }} />
      ) : (
        <ErrorIcon color="error" sx={{ mr: 1 }} />
      )}
      <Typography>
        {label}: {isCompliant ? "Conforme" : "Non conforme"}
      </Typography>
    </Box>
  );

  // Ajout des fonctions d'icône et couleur pour les types de dégâts
  const getDamageIcon = (type: string) => {
    const normalizedType = type === "Cassé" ? "Casse" : type;

    switch (normalizedType) {
      case "Rayure":
      case "rayures":
      case "rayure":
        return <ScatterPlotIcon sx={{ color: "white", fontSize: 16 }} />;
      case "Choc":
      case "chocs":
      case "choc":
        return <WarningIcon sx={{ color: "white", fontSize: 16 }} />;
      case "Manquant":
      case "manquant":
        return <ErrorOutlineIcon sx={{ color: "white", fontSize: 16 }} />;
      case "Cassé":
      case "Casse":
      case "casse":
        return <BuildIcon sx={{ color: "white", fontSize: 16 }} />;
      default:
        return <WarningIcon sx={{ color: "white", fontSize: 16 }} />;
    }
  };
  const getDamageColor = (type: string) => {
    const normalizedType = type === "Cassé" ? "Casse" : type;

    switch (normalizedType) {
      case "Rayure":
      case "rayures":
      case "rayure":
        return "#FFA500"; // Orange
      case "Choc":
      case "chocs":
      case "choc":
        return "#FF0000"; // Rouge
      case "Manquant":
      case "manquant":
        return "#0000FF"; // Bleu
      case "Cassé":
      case "Casse":
      case "casse":
        return "#800080"; // Violet
      default:
        return "#FF0000";
    }
  };

  // Fonction pour mapper une face API vers son nom de fichier image
  const mapFaceToImageName = (face: string): string => {
    const mapping: Record<string, string> = {
      avant: "/images/vehicles/face.png",
      arriere: "/images/vehicles/derriere.png",
      coteConducteur: "/images/vehicles/gauche.png",
      cotePassager: "/images/vehicles/droite.png",
      gauche: "/images/vehicles/gauche.png",
      droite: "/images/vehicles/droite.png",
      toit: "/images/vehicles/toit.png",
    };
    return mapping[face] || "/images/vehicles/face.png";
  };

  // Afficher les détails des dégradations
  const renderDegradationsDetails = (
    degradations:
      | {
          count: number;
          details: {
            rayures?: number;
            chocs?: number;
            manquant?: number;
            casse?: number;
            Rayure?: number;
            Choc?: number;
            Manquant?: number;
            Cassé?: number;
          };
          images?: {
            url: string;
            damageType: string;
            face: string;
            template?: string;
            position?: { x: number; y: number };
            points?: Array<{
              x: number;
              y: number;
              type: string;
              face: string;
            }>;
          }[];
        }
      | undefined,
    isInitialState: boolean
  ) => {
    if (!degradations) return null;

    console.log(
      `[Debriefing] Dégradations ${
        isInitialState ? "initiales" : "finales"
      } reçues:`,
      degradations
    );

    // Pour déboguer, voyons les images et leurs types de dégâts
    if (degradations.images && degradations.images.length > 0) {
      console.log(
        `[Debriefing] Types de dégâts dans les images:`,
        degradations.images.map((img) => ({
          damageType: img.damageType,
          face: img.face,
          position: img.position,
          points: img.points,
        }))
      );
    }

    // Vérifions aussi le contenu brut de details
    console.log(`[Debriefing] Détails bruts:`, degradations.details);

    // Vérifier si les détails existent et sont des nombres valides
    const details = {
      rayures: Number(
        degradations.details?.rayures || degradations.details?.Rayure || 0
      ),
      chocs: Number(
        degradations.details?.chocs || degradations.details?.Choc || 0
      ),
      manquant: Number(
        degradations.details?.manquant || degradations.details?.Manquant || 0
      ),
      casse: Number(
        degradations.details?.casse || degradations.details?.Cassé || 0
      ),
    };

    // Recalculer les nombres à partir des images (pour s'assurer que les compteurs sont corrects)
    const calculatedDetails = {
      rayures: 0,
      chocs: 0,
      manquant: 0,
      casse: 0,
    };

    if (degradations.images && degradations.images.length > 0) {
      degradations.images.forEach((img) => {
        // Normaliser le type
        const normalizedType =
          img.damageType === "Cassé" ? "Casse" : img.damageType;

        // Incrémenter le compteur approprié
        if (normalizedType === "Rayure") calculatedDetails.rayures++;
        else if (normalizedType === "Choc") calculatedDetails.chocs++;
        else if (normalizedType === "Manquant") calculatedDetails.manquant++;
        else if (normalizedType === "Casse") calculatedDetails.casse++;

        // Comptabiliser aussi les points individuels si présents
        if (img.points && img.points.length > 0) {
          img.points.forEach((point) => {
            const normalizedPointType =
              point.type === "Cassé" ? "Casse" : point.type;

            if (normalizedPointType === "Rayure") calculatedDetails.rayures++;
            else if (normalizedPointType === "Choc") calculatedDetails.chocs++;
            else if (normalizedPointType === "Manquant")
              calculatedDetails.manquant++;
            else if (normalizedPointType === "Casse") calculatedDetails.casse++;
          });
        }
      });
    }

    console.log(
      `[Debriefing] Détails calculés à partir des images:`,
      calculatedDetails
    );

    // Utiliser les compteurs calculés si les compteurs originaux sont à 0
    const finalDetails = {
      rayures:
        details.rayures > 0 ? details.rayures : calculatedDetails.rayures,
      chocs: details.chocs > 0 ? details.chocs : calculatedDetails.chocs,
      manquant:
        details.manquant > 0 ? details.manquant : calculatedDetails.manquant,
      casse: details.casse > 0 ? details.casse : calculatedDetails.casse,
    };

    console.log(
      `[Debriefing] Détails des dégradations ${
        isInitialState ? "initiales" : "finales"
      } après conversion:`,
      finalDetails
    );

    // Vérifier si nous avons des images avec des dégâts
    const hasImages = degradations.images && degradations.images.length > 0;
    const hasDegradations =
      finalDetails.rayures > 0 ||
      finalDetails.chocs > 0 ||
      finalDetails.manquant > 0 ||
      finalDetails.casse > 0 ||
      hasImages;

    // Regrouper les dégâts par face du véhicule
    const damagesByFace: Record<string, typeof degradations.images> = {};

    if (hasImages && degradations.images) {
      degradations.images.forEach((image) => {
        if (!damagesByFace[image.face]) {
          damagesByFace[image.face] = [];
        }
        damagesByFace[image.face]?.push(image);
      });
    }

    console.log(
      `[Debriefing] Dégâts ${
        isInitialState ? "initiaux" : "finaux"
      } regroupés par face:`,
      damagesByFace
    );

    // Définir les informations de tous les types de dégâts à afficher
    const damageTypes = [
      {
        key: "rayures",
        label: "Rayures",
        value: finalDetails.rayures,
        icon: "Rayure",
        color: getDamageColor("Rayure"),
      },
      {
        key: "chocs",
        label: "Chocs",
        value: finalDetails.chocs,
        icon: "Choc",
        color: getDamageColor("Choc"),
      },
      {
        key: "manquant",
        label: "Éléments manquants",
        value: finalDetails.manquant,
        icon: "Manquant",
        color: getDamageColor("Manquant"),
      },
      {
        key: "casse",
        label: "Éléments cassés",
        value: finalDetails.casse,
        icon: "Casse",
        color: getDamageColor("Casse"),
      },
    ];

    return (
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          {hasDegradations
            ? `Dégradations constatées ${
                isInitialState ? "à l'entrée" : "au retour"
              }:`
            : `Aucune dégradation constatée ${
                isInitialState ? "à l'entrée" : "au retour"
              }`}
        </Typography>
        {hasDegradations && (
          <Box sx={{ ml: 2 }}>
            {/* Afficher tous les types de dégâts, même ceux à 0 */}
            {damageTypes.map((damageType) => (
              <Box
                key={damageType.key}
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    backgroundColor: damageType.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {getDamageIcon(damageType.icon)}
                </Box>
                <Typography variant="body2">
                  {damageType.label}: {damageType.value}
                </Typography>
              </Box>
            ))}
            {hasImages && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Photos et localisation des dégâts :
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {/* Afficher les dégâts regroupés par face */}
                  {Object.entries(damagesByFace).map(([face, damages]) => (
                    <Box key={face} sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Face: {face}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          gap: 3,
                          flexWrap: "wrap",
                          alignItems: "start",
                        }}
                      >
                        {/* Template avec tous les points de dégâts */}
                        <Box
                          sx={{ position: "relative", width: 200, height: 150 }}
                        >
                          <img
                            src={
                              (damages && damages[0]?.template) ||
                              mapFaceToImageName(face)
                            }
                            alt={`Template ${face}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                          />

                          {/* Afficher tous les marqueurs de dégâts sur cette face */}
                          {damages &&
                            damages.map((damage, damageIndex) => {
                              // Utiliser soit position directe, soit points (pour rétrocompatibilité)
                              const damagePositions = [];

                              if (damage.position) {
                                damagePositions.push({
                                  x: damage.position.x,
                                  y: damage.position.y,
                                  type: damage.damageType,
                                  key: `direct-${damageIndex}`,
                                });
                              } else if (
                                damage.points &&
                                damage.points.length > 0
                              ) {
                                damage.points.forEach((point, pointIndex) => {
                                  damagePositions.push({
                                    x: point.x,
                                    y: point.y,
                                    type: point.type,
                                    key: `point-${damageIndex}-${pointIndex}`,
                                  });
                                });
                              }

                              return damagePositions.map((position) => (
                                <Box
                                  key={position.key}
                                  sx={{
                                    position: "absolute",
                                    left: `${position.x}%`,
                                    top: `${position.y}%`,
                                    transform: "translate(-50%, -50%)",
                                    width: 16,
                                    height: 16,
                                    borderRadius: "50%",
                                    backgroundColor: getDamageColor(
                                      position.type
                                    ),
                                    border: "2px solid #fff",
                                    boxShadow: "0 0 4px rgba(0,0,0,0.3)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    zIndex: 2,
                                  }}
                                  title={position.type}
                                >
                                  {getDamageIcon(position.type)}
                                </Box>
                              ));
                            })}
                        </Box>

                        {/* Supprimer l'affichage des photos individuelles des dégâts */}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !assignment) {
    return (
      <Container maxWidth="md">
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" color="error">
            {error || "Affectation non trouvée"}
          </Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/assignments")}
            sx={{ mt: 2 }}
          >
            Retour à la liste
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ width: "100vw", minHeight: "100vh", p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/assignments")}
          sx={{ mr: 2 }}
        >
          Retour
        </Button>
        <Typography variant="h4" fontWeight={600}>
          Débriefing et finalisation
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Information sur l'affectation
          </Typography>
          <Grid container spacing={2}>
            <Grid sx={{ gridColumn: { xs: "1 / 13", sm: "1 / 7" } }}>
              <Typography variant="body1">
                <strong>Véhicule:</strong> {assignment.vehiclePlate}
              </Typography>
            </Grid>
            <Grid sx={{ gridColumn: { xs: "1 / 13", sm: "7 / 13" } }}>
              <Typography variant="body1">
                <strong>Chauffeur:</strong> {assignment.driverFirstName}{" "}
                {assignment.driverLastName}
              </Typography>
            </Grid>
            <Grid sx={{ gridColumn: { xs: "1 / 13", sm: "1 / 7" } }}>
              <Typography variant="body1">
                <strong>Date de début:</strong>{" "}
                {new Date(assignment.date).toLocaleDateString("fr-FR")}
              </Typography>
            </Grid>
            <Grid sx={{ gridColumn: { xs: "1 / 13", sm: "7 / 13" } }}>
              <Typography variant="body1">
                <strong>Kilométrage de départ:</strong>{" "}
                {assignment.startMileage} km
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Résumé de l'état initial
        </Typography>

        {assignment.initialState && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Propreté:
            </Typography>
            <Box sx={{ ml: 2, mb: 2 }}>
              <Typography variant="body2">
                Intérieur:{" "}
                {assignment.initialState.cleanliness.interior
                  ? "Propre"
                  : "Sale"}
              </Typography>
              <Typography variant="body2">
                Extérieur:{" "}
                {assignment.initialState.cleanliness.exterior
                  ? "Propre"
                  : "Sale"}
              </Typography>
              <Typography variant="body2">
                Cabine:{" "}
                {assignment.initialState.cleanliness.cabine ? "Propre" : "Sale"}
              </Typography>
              {assignment.initialState.cleanliness.comments && (
                <Typography variant="body2">
                  Commentaires: {assignment.initialState.cleanliness.comments}
                </Typography>
              )}
            </Box>

            <Typography variant="subtitle1" gutterBottom>
              Contrôles techniques:
            </Typography>
            <Box sx={{ ml: 2, mb: 2 }}>
              {renderComplianceStatus(
                assignment.initialState.checklist.lights === "conforme",
                "Éclairage"
              )}
              {renderComplianceStatus(
                assignment.initialState.checklist.brakes === "conforme",
                "Système de freinage"
              )}
              {renderComplianceStatus(
                assignment.initialState.checklist.tires === "conforme",
                "Pneumatiques"
              )}
              {renderComplianceStatus(
                assignment.initialState.checklist.documents === "conforme",
                "Documents de bord"
              )}
              {assignment.initialState.checklist.comments && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Commentaires: {assignment.initialState.checklist.comments}
                </Typography>
              )}
            </Box>

            {renderDegradationsDetails(
              assignment.initialState.degradations,
              true
            )}
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Résumé de l'état final
        </Typography>

        {assignment.finalState && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Propreté:
            </Typography>
            <Box sx={{ ml: 2, mb: 2 }}>
              <Typography variant="body2">
                Intérieur:{" "}
                {assignment.finalState.cleanliness.interior ? "Propre" : "Sale"}
              </Typography>
              <Typography variant="body2">
                Extérieur:{" "}
                {assignment.finalState.cleanliness.exterior ? "Propre" : "Sale"}
              </Typography>
              <Typography variant="body2">
                Cabine:{" "}
                {assignment.finalState.cleanliness.cabine ? "Propre" : "Sale"}
              </Typography>
              {assignment.finalState.cleanliness.comments && (
                <Typography variant="body2">
                  Commentaires: {assignment.finalState.cleanliness.comments}
                </Typography>
              )}
            </Box>

            <Typography variant="subtitle1" gutterBottom>
              Contrôles techniques:
            </Typography>
            <Box sx={{ ml: 2, mb: 2 }}>
              {renderComplianceStatus(
                assignment.finalState.checklist.lights === "conforme",
                "Éclairage"
              )}
              {renderComplianceStatus(
                assignment.finalState.checklist.brakes === "conforme",
                "Système de freinage"
              )}
              {renderComplianceStatus(
                assignment.finalState.checklist.tires === "conforme",
                "Pneumatiques"
              )}
              {renderComplianceStatus(
                assignment.finalState.checklist.documents === "conforme",
                "Documents de bord"
              )}
              {assignment.finalState.checklist.comments && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Commentaires: {assignment.finalState.checklist.comments}
                </Typography>
              )}
            </Box>

            {renderDegradationsDetails(
              assignment.finalState.degradations,
              false
            )}
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Informations de retour
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid sx={{ gridColumn: { xs: "1 / 13", sm: "1 / 7" } }}>
                <TextField
                  fullWidth
                  label="Date de retour"
                  type="date"
                  name="endDate"
                  value={endData.endDate}
                  onChange={handleInputChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid sx={{ gridColumn: { xs: "1 / 13", sm: "7 / 13" } }}>
                <TextField
                  fullWidth
                  label="Heure de retour"
                  type="time"
                  name="endTime"
                  value={endData.endTime}
                  onChange={handleInputChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid sx={{ gridColumn: { xs: "1 / 13", sm: "1 / 7" } }}>
                <TextField
                  fullWidth
                  label="Kilométrage final"
                  type="number"
                  name="endMileage"
                  value={endData.endMileage}
                  onChange={handleInputChange}
                  error={endData.endMileage < (assignment.startMileage || 0)}
                  helperText={
                    endData.endMileage < (assignment.startMileage || 0)
                      ? "Le kilométrage final doit être supérieur au kilométrage initial"
                      : ""
                  }
                />
              </Grid>
              <Grid sx={{ gridColumn: { xs: "1 / 13", sm: "7 / 13" } }}>
                <FormControl fullWidth>
                  <InputLabel id="fuel-level-label">
                    Niveau de carburant final
                  </InputLabel>
                  <Select
                    labelId="fuel-level-label"
                    name="endFuelLevel"
                    value={endData.endFuelLevel}
                    onChange={handleSelectChange}
                    label="Niveau de carburant final"
                  >
                    {fuelLevelOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Commentaires"
              name="comments"
              value={endData.comments}
              onChange={handleInputChange}
              multiline
              rows={3}
              placeholder="Ajouter des commentaires supplémentaires sur le retour du véhicule..."
            />
          </Stack>
        </Box>

        {/* Section retour équipement de manutention */}
        {assignment.equipment?.taken && (
          <>
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: "#3B82F6",
                  display: "flex"
                }}>
                  <Forklift size={28} color="white" />
                </Box>
                <Box>
                  <Typography variant="h6">
                    Retour de l'équipement de manutention
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {assignment.equipment.equipmentNumber || assignment.equipment.equipmentType?.replace(/_/g, ' ')}
                  </Typography>
                </Box>
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  Un équipement de manutention ({assignment.equipment.equipmentType?.replace(/_/g, ' ')}) a été confié au chauffeur lors de cette affectation.
                  Veuillez confirmer son retour.
                </Typography>
              </Alert>

              <Paper elevation={0} sx={{ p: 3, bgcolor: "#F9FAFB", borderRadius: 2 }}>
                <FormControl component="fieldset" sx={{ width: "100%" }}>
                  <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
                    État du retour de l'équipement
                  </FormLabel>
                  <RadioGroup
                    value={equipmentReturn.condition}
                    onChange={(e) => setEquipmentReturn(prev => ({
                      ...prev,
                      condition: e.target.value as "bon_etat" | "endommagé" | "non_rendu",
                      returned: e.target.value !== "non_rendu"
                    }))}
                  >
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 2, 
                        mb: 1, 
                        border: equipmentReturn.condition === "bon_etat" ? "2px solid #22C55E" : "1px solid #E5E7EB",
                        borderRadius: 2,
                        bgcolor: equipmentReturn.condition === "bon_etat" ? "#F0FDF4" : "white",
                        cursor: "pointer"
                      }}
                      onClick={() => setEquipmentReturn(prev => ({ ...prev, condition: "bon_etat", returned: true }))}
                    >
                      <FormControlLabel 
                        value="bon_etat" 
                        control={<Radio color="success" />} 
                        label={
                          <Box>
                            <Typography fontWeight={600} color="success.dark">✓ Rendu en bon état</Typography>
                            <Typography variant="body2" color="text.secondary">
                              L'équipement est retourné sans dommage
                            </Typography>
                          </Box>
                        }
                      />
                    </Paper>

                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 2, 
                        mb: 1, 
                        border: equipmentReturn.condition === "endommagé" ? "2px solid #F59E0B" : "1px solid #E5E7EB",
                        borderRadius: 2,
                        bgcolor: equipmentReturn.condition === "endommagé" ? "#FFFBEB" : "white",
                        cursor: "pointer"
                      }}
                      onClick={() => setEquipmentReturn(prev => ({ ...prev, condition: "endommagé", returned: true }))}
                    >
                      <FormControlLabel 
                        value="endommagé" 
                        control={<Radio color="warning" />} 
                        label={
                          <Box>
                            <Typography fontWeight={600} color="warning.dark">⚠ Rendu avec dommages</Typography>
                            <Typography variant="body2" color="text.secondary">
                              L'équipement présente des dégâts
                            </Typography>
                          </Box>
                        }
                      />
                    </Paper>

                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 2, 
                        border: equipmentReturn.condition === "non_rendu" ? "2px solid #EF4444" : "1px solid #E5E7EB",
                        borderRadius: 2,
                        bgcolor: equipmentReturn.condition === "non_rendu" ? "#FEF2F2" : "white",
                        cursor: "pointer"
                      }}
                      onClick={() => setEquipmentReturn(prev => ({ ...prev, condition: "non_rendu", returned: false }))}
                    >
                      <FormControlLabel 
                        value="non_rendu" 
                        control={<Radio color="error" />} 
                        label={
                          <Box>
                            <Typography fontWeight={600} color="error.dark">✗ Non rendu / Perdu</Typography>
                            <Typography variant="body2" color="text.secondary">
                              L'équipement n'a pas été retourné ou a été perdu
                            </Typography>
                          </Box>
                        }
                      />
                    </Paper>
                  </RadioGroup>
                </FormControl>

                {(equipmentReturn.condition === "endommagé" || equipmentReturn.condition === "non_rendu") && (
                  <>
                    <TextField
                      fullWidth
                      label="Commentaire sur l'état de l'équipement"
                      value={equipmentReturn.comment}
                      onChange={(e) => setEquipmentReturn(prev => ({ ...prev, comment: e.target.value }))}
                      multiline
                      rows={2}
                      sx={{ mt: 2 }}
                      placeholder={
                        equipmentReturn.condition === "endommagé" 
                          ? "Décrivez les dommages constatés..." 
                          : "Expliquez pourquoi l'équipement n'a pas été rendu (perdu, oublié, volé, etc.)..."
                      }
                      required
                    />

                    {/* Upload de photos */}
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        Photos de preuve *
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        Ajoutez des photos pour documenter l'état de l'équipement (dommages, perte, etc.)
                      </Typography>

                      {/* Bouton d'upload */}
                      <input
                        accept="image/*"
                        style={{ display: "none" }}
                        id="equipment-photo-upload"
                        type="file"
                        multiple
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          const newPhotos = [...equipmentReturn.photos, ...files];
                          const newPreviews = newPhotos.map(file => URL.createObjectURL(file));
                          setEquipmentReturn(prev => ({
                            ...prev,
                            photos: newPhotos,
                            photoPreviews: newPreviews,
                          }));
                        }}
                      />
                      <label htmlFor="equipment-photo-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<PhotoCameraIcon />}
                          sx={{ mb: 2 }}
                        >
                          Ajouter des photos
                        </Button>
                      </label>

                      {/* Aperçu des photos */}
                      {equipmentReturn.photoPreviews.length > 0 && (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2 }}>
                          {equipmentReturn.photoPreviews.map((preview, index) => (
                            <Box
                              key={index}
                              sx={{
                                position: "relative",
                                width: 150,
                                height: 150,
                                borderRadius: 2,
                                overflow: "hidden",
                                border: "2px solid",
                                borderColor: "divider",
                              }}
                            >
                              <Box
                                component="img"
                                src={preview}
                                alt={`Photo ${index + 1}`}
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const newPhotos = equipmentReturn.photos.filter((_, i) => i !== index);
                                  const newPreviews = equipmentReturn.photoPreviews.filter((_, i) => i !== index);
                                  // Libérer l'URL de l'objet
                                  URL.revokeObjectURL(preview);
                                  setEquipmentReturn(prev => ({
                                    ...prev,
                                    photos: newPhotos,
                                    photoPreviews: newPreviews,
                                  }));
                                }}
                                sx={{
                                  position: "absolute",
                                  top: 4,
                                  right: 4,
                                  bgcolor: "error.main",
                                  color: "white",
                                  "&:hover": {
                                    bgcolor: "error.dark",
                                  },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {equipmentReturn.photos.length === 0 && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          Au moins une photo est requise pour documenter l'état de l'équipement
                        </Alert>
                      )}
                    </Box>
                  </>
                )}
              </Paper>

              {/* Résumé visuel */}
              <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                <Chip 
                  label={`Équipement: ${assignment.equipment.equipmentNumber || assignment.equipment.equipmentType?.replace(/_/g, ' ')}`}
                  variant="outlined"
                />
                <Chip 
                  label={
                    equipmentReturn.condition === "bon_etat" ? "Rendu ✓" :
                    equipmentReturn.condition === "endommagé" ? "Endommagé ⚠" :
                    "Non rendu / Perdu ✗"
                  }
                  color={
                    equipmentReturn.condition === "bon_etat" ? "success" :
                    equipmentReturn.condition === "endommagé" ? "warning" :
                    "error"
                  }
                />
              </Box>
            </Box>
          </>
        )}
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Button variant="outlined" onClick={() => navigate("/assignments")}>
          Annuler
        </Button>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handlePreviousStep}
            disabled={submitting}
          >
            Étape précédente
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleFinalize}
            disabled={submitting || !isFormValid()}
          >
            {submitting ? "Finalisation..." : "Finaliser l'affectation"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Debriefing;
